import Phaser from 'phaser';
import { PALETTE } from './palette';
import { TEX } from './textures';

/**
 * 画面効果。ゲームの状態は一切持たず、シミュレーションから渡された事実を絵にするだけ。
 *
 * 狙いは 2 つ。
 *  - **水に見せる。** 平坦な塗りにコースティクスと周辺減光を重ねると、
 *    同じ色でも「塗られた面」から「水」に変わる。
 *  - **潜ったことを体で分かるようにする。** 画面全体が水中色に沈み、泡が上がる。
 *    ダイブはこのゲームの中心動作なので、押した手応えが要る。
 */
export class Effects {
  private readonly scene: Phaser.Scene;
  private readonly caustics: Phaser.GameObjects.TileSprite;
  private readonly underwater: Phaser.GameObjects.Rectangle;
  private readonly vignette: Phaser.GameObjects.Image;
  private readonly flash: Phaser.GameObjects.Rectangle;

  private readonly splash: Phaser.GameObjects.Particles.ParticleEmitter;
  private readonly bubbles: Phaser.GameObjects.Particles.ParticleEmitter;
  private readonly spray: Phaser.GameObjects.Particles.ParticleEmitter;
  private readonly sparkle: Phaser.GameObjects.Particles.ParticleEmitter;

  /** カメラの振り分けに使う。世界側カメラだけに映す。 */
  readonly objects: Phaser.GameObjects.GameObject[] = [];

  constructor(scene: Phaser.Scene, width: number, height: number, depthBase: number) {
    this.scene = scene;

    // 水面の光の網目。水深に関係なく全面に薄くかけ、スクロールに合わせて流す。
    this.caustics = scene.add
      .tileSprite(width / 2, height / 2, width, height, TEX.caustics)
      .setDepth(depthBase)
      .setBlendMode(Phaser.BlendModes.ADD)
      .setAlpha(0.06);
    // 大きく引き伸ばして、タイルの継ぎ目と反復を目立たせない。
    this.caustics.tileScaleX = 1.6;
    this.caustics.tileScaleY = 1.6;

    // 潜行中の水中。全面を沈める。
    this.underwater = scene.add
      .rectangle(width / 2, height / 2, width, height, PALETTE.mid, 1)
      .setDepth(depthBase + 4)
      .setAlpha(0);

    this.vignette = scene.add
      .image(width / 2, height / 2, TEX.vignette)
      .setDisplaySize(width, height)
      .setDepth(depthBase + 5)
      .setAlpha(0.34);

    // ドルフィンスルー成功などの一瞬の閃光。
    this.flash = scene.add
      .rectangle(width / 2, height / 2, width, height, PALETTE.foam, 1)
      .setDepth(depthBase + 6)
      .setAlpha(0);

    const particleDepth = depthBase + 3;

    this.splash = scene.add
      .particles(0, 0, TEX.dot, {
        speed: { min: 60, max: 220 },
        angle: { min: 200, max: 340 },
        scale: { start: 0.5, end: 0 },
        alpha: { start: 0.9, end: 0 },
        lifespan: { min: 260, max: 620 },
        gravityY: 260,
        blendMode: Phaser.BlendModes.ADD,
        emitting: false,
      })
      .setDepth(particleDepth);

    this.bubbles = scene.add
      .particles(0, 0, TEX.dot, {
        speed: { min: 20, max: 70 },
        angle: { min: 250, max: 290 },
        scale: { start: 0.22, end: 0.04 },
        alpha: { start: 0.55, end: 0 },
        lifespan: { min: 500, max: 1100 },
        frequency: 45,
        blendMode: Phaser.BlendModes.ADD,
        emitting: false,
      })
      .setDepth(particleDepth);

    // 波に乗っている間、後ろへ散り続けるしぶき。
    this.spray = scene.add
      .particles(0, 0, TEX.dot, {
        speed: { min: 30, max: 130 },
        angle: { min: 60, max: 120 },
        scale: { start: 0.34, end: 0 },
        alpha: { start: 0.7, end: 0 },
        lifespan: { min: 220, max: 480 },
        frequency: 28,
        blendMode: Phaser.BlendModes.ADD,
        emitting: false,
      })
      .setDepth(particleDepth);

    this.sparkle = scene.add
      .particles(0, 0, TEX.spark, {
        speed: { min: 40, max: 150 },
        scale: { start: 0.45, end: 0 },
        alpha: { start: 1, end: 0 },
        rotate: { start: 0, end: 180 },
        lifespan: { min: 320, max: 700 },
        blendMode: Phaser.BlendModes.ADD,
        emitting: false,
      })
      .setDepth(particleDepth);

    this.objects.push(
      this.caustics,
      this.underwater,
      this.vignette,
      this.flash,
      this.splash,
      this.bubbles,
      this.spray,
      this.sparkle,
    );
  }

  /**
   * 毎フレームの更新。
   * @param scrollPx 世界のスクロール量（px）。コースティクスをこれに追従させる。
   */
  update(params: {
    scrollPx: number;
    timeMs: number;
    diving: boolean;
    riding: boolean;
    swimmerX: number;
    swimmerY: number;
    depthRatio: number;
  }): void {
    const { scrollPx, timeMs, diving, riding, swimmerX, swimmerY, depthRatio } = params;

    // 進むと下へ流れる。ゆっくり横にも揺らして、静止画に見えないようにする。
    this.caustics.tilePositionY = scrollPx / 1.6;
    this.caustics.tilePositionX = Math.sin(timeMs / 3600) * 18;
    // 浅いほど光が届いて網目がはっきりする。濃くしすぎると水面ではなく模様に見える。
    this.caustics.setAlpha(0.03 + 0.055 * (1 - depthRatio) + (diving ? 0.05 : 0));

    this.underwater.setAlpha(
      Phaser.Math.Linear(this.underwater.alpha, diving ? 0.42 : 0, 0.18),
    );

    if (riding && !diving) {
      this.spray.emitParticleAt(swimmerX, swimmerY);
    }
    if (diving) {
      this.bubbles.emitParticleAt(swimmerX, swimmerY);
    }
  }

  /** 潜った瞬間・浮上した瞬間の水しぶき。 */
  splashAt(x: number, y: number, amount = 14): void {
    this.splash.explode(amount, x, y);
  }

  /** 巻かれたときの泡の爆ぜ。 */
  foamBurst(x: number, y: number): void {
    this.splash.explode(30, x, y);
    this.sparkle.explode(6, x, y);
  }

  /** 宝の取得。 */
  sparkleAt(x: number, y: number, amount = 8): void {
    this.sparkle.explode(amount, x, y);
  }

  /** ドルフィンスルー成功。短く光らせる。 */
  flashScreen(alpha = 0.22, duration = 220): void {
    this.flash.setAlpha(alpha);
    this.scene.tweens.killTweensOf(this.flash);
    this.scene.tweens.add({ targets: this.flash, alpha: 0, duration });
  }

  /** 走行開始時に持ち越しを消す。 */
  reset(): void {
    this.underwater.setAlpha(0);
    this.flash.setAlpha(0);
    this.splash.killAll();
    this.bubbles.killAll();
    this.spray.killAll();
    this.sparkle.killAll();
  }
}
